import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Interactive Header Preview Component
 * Allows drag-and-drop positioning of header elements
 */
const InteractiveHeaderPreview = ({ templateConfig, companySettings, onConfigChange }) => {
  const [activeId, setActiveId] = useState(null);
  const [editingField, setEditingField] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get enabled header fields
  const getEnabledFields = () => {
    if (!templateConfig?.header) return [];

    const fields = [];
    const fieldOrder = ['logo', 'companyName', 'companyAddress', 'contact', 'email', 'uen', 'bankDetails'];

    fieldOrder.forEach(field => {
      const config = templateConfig.header[field];
      if (config && config.enabled) {
        fields.push({
          field,
          config,
          label: getFieldLabel(field)
        });
      }
    });

    return fields;
  };

  const getFieldLabel = (field) => {
    const labels = {
      logo: 'Logo',
      companyName: 'Company Name',
      companyAddress: 'Company Address',
      contact: 'Contact',
      email: 'Email',
      uen: 'GST',
      bankDetails: 'Bank Details'
    };
    return labels[field] || field;
  };

  const getFieldValue = (field) => {
    if (!companySettings) return '';

    switch (field) {
      case 'logo':
        return companySettings.logoUrl || '';
      case 'companyName':
        return companySettings.name || '';
      case 'companyAddress':
        return `${companySettings.addressLine1 || ''} ${companySettings.addressLine2 || ''}`.trim();
      case 'contact':
        return companySettings.contact || '';
      case 'email':
        return companySettings.email || '';
      case 'uen':
        return companySettings.uen || '';
      case 'bankDetails':
        return companySettings.bankName || '';
      default:
        return '';
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const enabledFields = getEnabledFields();
    const oldIndex = enabledFields.findIndex(f => f.field === active.id);
    const newIndex = enabledFields.findIndex(f => f.field === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newFields = arrayMove(enabledFields, oldIndex, newIndex);
      updateFieldOrder(newFields);
    }
  };

  const updateFieldOrder = (newFields) => {
    if (!templateConfig) return;

    const newConfig = JSON.parse(JSON.stringify(templateConfig));

    // Update positions based on new order
    newFields.forEach((field, index) => {
      if (newConfig.header[field.field]) {
        // Store order in config
        newConfig.header[field.field].order = index;
      }
    });

    onConfigChange(newConfig);
  };

  const handlePositionChange = (field, newPosition) => {
    if (!templateConfig) return;

    const newConfig = JSON.parse(JSON.stringify(templateConfig));
    if (newConfig.header[field]) {
      newConfig.header[field].position = newPosition;
      onConfigChange(newConfig);
    }
  };

  const handleToggleField = (field) => {
    if (!templateConfig) return;

    const newConfig = JSON.parse(JSON.stringify(templateConfig));
    if (newConfig.header[field]) {
      newConfig.header[field].enabled = !newConfig.header[field].enabled;
      onConfigChange(newConfig);
    }
  };

  const enabledFields = getEnabledFields();
  const fieldIds = enabledFields.map(f => f.field);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h6" gutterBottom>
          Header Layout
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drag elements to reorder. Use position buttons to move between Left, Center, and Right.
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
              {enabledFields.map(({ field, config }) => (
                <SortableHeaderField
                  key={field}
                  id={field}
                  field={field}
                  config={config}
                  value={getFieldValue(field)}
                  label={getFieldLabel(field)}
                  onPositionChange={handlePositionChange}
                  onToggle={handleToggleField}
                />
              ))}
            </Box>
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <Paper
                sx={{
                  p: 2,
                  boxShadow: 3,
                  opacity: 0.8,
                  transform: 'rotate(5deg)',
                  cursor: 'grabbing'
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  {getFieldLabel(activeId)}
                </Typography>
              </Paper>
            ) : null}
          </DragOverlay>
        </DndContext>

        {enabledFields.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              No header fields enabled. Enable fields from the left sidebar.
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

// Sortable Header Field Component
const SortableHeaderField = ({ id, field, config, value, label, onPositionChange, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const currentPosition = config.position || 'right';

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        border: 1,
        borderColor: 'divider',
        '&:hover': {
          boxShadow: 2
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              '&:active': {
                cursor: 'grabbing'
              }
            }}
          >
            <DragIcon color="action" />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {field === 'logo' && value ? 'Logo Image' : value || 'No value'}
            </Typography>
            <Chip
              label={currentPosition}
              size="small"
              color={currentPosition === 'center' ? 'primary' : 'default'}
              sx={{ mt: 1 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label="Left"
              size="small"
              variant={currentPosition === 'left' ? 'filled' : 'outlined'}
              onClick={() => onPositionChange(field, 'left')}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Center"
              size="small"
              variant={currentPosition === 'center' ? 'filled' : 'outlined'}
              onClick={() => onPositionChange(field, 'center')}
              color="primary"
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Right"
              size="small"
              variant={currentPosition === 'right' ? 'filled' : 'outlined'}
              onClick={() => onPositionChange(field, 'right')}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default InteractiveHeaderPreview;

